import { useState, useEffect } from "react"
import { AlertCircle, Eye, MapPin, User } from "lucide-react"
import { Button } from "../components/ui/button"
import { API_ENDPOINTS } from "../config/api"
import { fetchWithAuth } from "../utils/api"
import { useLanguage } from "../i18n/LanguageContext"

export default function BookingManagement() {
  const { t, translateStatus, translateService, translateApiMessage, formatNumber, formatCurrency } = useLanguage()
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    fetchBookings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter])

  const fetchBookings = async () => {
    try {
      let url = `${API_ENDPOINTS.BOOKINGS}?page=${page}&per_page=10`

      if (statusFilter) {
        url += `&status=${statusFilter}`
      }

      const res = await fetchWithAuth(url)

      if (res.ok) {
        const data = await res.json()
        setBookings(data.bookings)
        setTotalPages(data.pages)
      } else {
        setError(t("errors.fetchBookings"))
      }
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.fetchBookings"))
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-800"
      case "Accepted":
        return "bg-blue-100 text-blue-800"
      case "EnRoute":
        return "bg-purple-100 text-purple-800"
      case "Arrived":
        return "bg-indigo-100 text-indigo-800"
      case "Completed":
        return "bg-green-100 text-green-800"
      case "Cancelled":
        return "bg-red-100 text-red-800"
      case "Disputed":
        return "bg-orange-100 text-orange-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="management-title text-3xl font-bold text-gray-800">{t("bookings.title")}</h1>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="">{t("common.allStatuses")}</option>
            <option value="Pending">{t("status.pending")}</option>
            <option value="Accepted">{t("status.accepted")}</option>
            <option value="EnRoute">{t("status.enroute")}</option>
            <option value="Arrived">{t("status.arrived")}</option>
            <option value="Completed">{t("status.completed")}</option>
            <option value="Cancelled">{t("status.cancelled")}</option>
            <option value="Disputed">{t("status.disputed")}</option>
          </select>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="data-table-container bg-white rounded-lg shadow">
        <table className="data-table w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("bookings.bookingId")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.user")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.captain")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("bookings.service")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("bookings.fare")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.status")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t("common.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {bookings.length > 0 ? (
              bookings.map((booking) => (
                <tr key={booking.booking_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{booking.booking_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {booking.user_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {booking.captain_name || t("common.unassigned")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {translateService(booking.service_type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatCurrency(booking.final_fare ?? booking.estimated_fare ?? 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                        booking.status
                      )}`}>
                      {translateStatus(booking.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => {
                        setSelectedBooking(booking)
                        setShowModal(true)
                      }}
                      className="text-amber-600 hover:text-amber-800 font-medium">
                      <Eye className="w-4 h-4 inline" /> {t("common.view")}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  {t("bookings.noBookings")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {t("common.pageOf", { page: formatNumber(page), total: formatNumber(totalPages) })}
        </p>
        <div className="flex gap-2">
          <Button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 disabled:opacity-50">
            {t("common.previous")}
          </Button>
          <Button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 disabled:opacity-50">
            {t("common.next")}
          </Button>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="responsive-modal bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {t("bookings.bookingNumber", { id: selectedBooking.booking_id })}
              </h2>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <User className="w-4 h-4" /> {t("common.user")}
                  </p>
                  <p className="font-medium text-gray-800">{selectedBooking.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <User className="w-4 h-4" /> {t("common.captain")}
                  </p>
                  <p className="font-medium text-gray-800">
                    {selectedBooking.captain_name || t("common.unassigned")}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("bookings.serviceType")}</p>
                  <p className="font-medium text-gray-800">{translateService(selectedBooking.service_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("common.status")}</p>
                  <p
                    className={`font-medium px-3 py-1 rounded-full text-xs inline-block ${getStatusColor(
                      selectedBooking.status
                    )}`}>
                    {translateStatus(selectedBooking.status)}
                  </p>
                </div>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {t("bookings.route")}
                </h3>
                <p className="text-sm text-gray-600">{t("bookings.from")}: {selectedBooking.pickup_address}</p>
                <p className="text-sm text-gray-600">{t("bookings.to")}: {selectedBooking.dropoff_address}</p>
                <p className="text-sm text-gray-600 mt-2">
                  {t("bookings.distance")}: {selectedBooking.distance_km == null ? t("common.notAvailable") : `${formatNumber(selectedBooking.distance_km, { maximumFractionDigits: 2 })} ${t("common.kilometres")}`}
                </p>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3">{t("bookings.pricing")}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("bookings.estimatedFare")}:</span>
                    <span className="font-medium">
                      {formatCurrency(selectedBooking.estimated_fare || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("bookings.finalFare")}:</span>
                    <span className="font-medium">
                      {selectedBooking.final_fare == null ? t("common.notAvailable") : formatCurrency(selectedBooking.final_fare)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">{t("bookings.appCommission")}:</span>
                    <span className="font-medium text-amber-600">
                      {formatCurrency(selectedBooking.app_commission || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("bookings.captainEarning")}:</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(selectedBooking.captain_earning || 0)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium">
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
